import config from '@/config';
import { currentUserState } from '@/state';
import type { Company, CompanyId } from '@rock/types';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

/**
 * Fetches a company map or photo via the authed cloud function proxy and returns an object URL
 * suitable for `<img src>`. Keeps the Google API key server-side. Revokes the URL on cleanup.
 * Returns undefined while loading or if the company has nothing to proxy.
 */
export function useCompanyImage(company: Company | undefined, kind: 'map' | 'photo'): string | undefined {
    const user = useAtomValue(currentUserState);
    const [objectUrl, setObjectUrl] = useState<string | undefined>(undefined);

    const needed = kind === 'map' ? !!company?.geo : !!company?.googlePhotoRef;
    const companyId = company?.id;

    useEffect(() => {
        if (!user || !companyId || !needed) {
            setObjectUrl(undefined);
            return;
        }
        let cancelled = false;
        let createdUrl: string | undefined;
        (async () => {
            try {
                const token = await user.getIdToken();
                const res = await fetch(buildProxyUrl(companyId, kind), {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!res.ok) return;
                const blob = await res.blob();
                if (cancelled) return;
                createdUrl = URL.createObjectURL(blob);
                setObjectUrl(createdUrl);
            } catch {
                if (!cancelled) setObjectUrl(undefined);
            }
        })();
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [user, companyId, kind, needed]);

    return objectUrl;
}

function buildProxyUrl(companyId: CompanyId, kind: 'map' | 'photo'): string {
    const base = `https://us-central1-${config.firebase.config.projectId}.cloudfunctions.net/companyImage`;
    const params = new URLSearchParams({ companyId, kind });
    return `${base}?${params.toString()}`;
}
